/**
 * goalData — research goal → compound bucketing for /goal/:slug landing pages
 * AND the `/alternatives-to/:slug` recommendation engine (Phase W).
 *
 * IMPORTANT: compound names MUST match `compounds.name` in the live DB
 * exactly (case-insensitive). The alternatives engine does:
 *
 *   g.compounds.some((n) => n.toLowerCase() === sourceCompound.name.toLowerCase())
 *
 * If a name here doesn't match a row in the compounds table, that compound
 * silently produces 0 alternatives. Audit caught BPC-157 only had 1
 * alternative because:
 *   - "Epitalon" was typo'd here, DB has "Epithalon"
 *   - "TB-500" here ≠ "TB-500 (Thymosin Beta-4)" in DB
 *   - "Pentadecarginine" wasn't in the DB at all
 *
 * Verification: scripts/verify-goalData.cjs cross-references this list
 * against the live compounds.name list and warns on misses.
 *
 * Last full audit: 2026-04-29 (217 compounds in DB, 12 goals here, ~75
 * unique compound names bucketed).
 */
export type GoalSlug =
  | "fat-loss"
  | "recovery"
  | "focus"
  | "longevity"
  | "sleep"
  | "skin-hair"
  | "energy"
  | "stress"
  | "dopamine"
  | "social"
  | "neuroplasticity"
  | "cognition";

export interface GoalDef {
  slug: GoalSlug;
  label: string;
  emoji: string;
  description: string;
  compounds: string[];
}

export const goalData: GoalDef[] = [
  {
    slug: "fat-loss",
    label: "Fat Loss",
    emoji: "🔥",
    description: "Compounds researched for metabolic enhancement and body composition.",
    // GLP-1s, GIP/GLP dual/triple agonists, AMPK activators, mitochondrial
    // uncouplers, growth hormone fragments, and select stimulants.
    compounds: [
      "Retatrutide",
      "Tesofensine",
      "Semaglutide",
      "Tirzepatide",
      "Mazdutide",
      "Orforglipron",
      "Cagrilintide",
      "Exenatide",
      "AOD-9604",
      "HGH Fragment 176-191",
      "Tesamorelin",
      "SLU-PP-332",
      "5-Amino-1MQ",
      "MOTS-c",
      "BAM15",
      "L-Carnitine",
      "Lipo-C",
      "Berberine",
      "Clenbuterol",
    ],
  },
  {
    slug: "recovery",
    label: "Recovery & Healing",
    emoji: "💪",
    description: "Peptides and compounds studied for tissue repair, healing, and regeneration.",
    compounds: [
      "BPC-157",
      "TB-500",
      "BPC-157/TB-500 Blend",
      "GHK-Cu",
      "KPV",
      "PDRN",
      "Thymosin Alpha-1 (TA1)",
      "Thymalin",
      "Thymogen",
      "LL-37",
      "Humanin",
      "ARA-290",
      "IGF-1 LR3",
      "PEG-MGF",
      "Cartalax",
      "Pinealon",
      "Livagen",
      "Cordyceps",
    ],
  },
  {
    slug: "focus",
    label: "Focus & Cognition",
    emoji: "🧠",
    description: "Nootropics and compounds researched for attention, focus, and working memory.",
    compounds: [
      "Modafinil",
      "Caffeine",
      "Semax",
      "N-Acetyl-Semax",
      "Selank",
      "N-Acetyl-Selank",
      "Semax/Selank Blend",
      "Bromantane",
      "Sulbutiamine",
      "Phenylpiracetam",
      "Noopept",
      "Piracetam",
      "Noopept + Choline Chloride Blend",
      "Alpha-GPC",
      "CDP-Choline",
      "L-Tyrosine",
      "L-Theanine",
      "Lion's Mane Mushroom",
      "Methylene Blue",
      "Theacrine",
      "Bacopa monnieri",
      "Rhodiola rosea",
      "Phenibut",
      "Dihexa",
      "NSI-189",
      "P-21",
      "TAK-653",
    ],
  },
  {
    slug: "cognition",
    label: "Memory & Long-term Cognition",
    emoji: "📚",
    description: "Compounds studied for memory consolidation, hippocampal health, and long-term cognitive function.",
    compounds: [
      "Dihexa",
      "Semax",
      "N-Acetyl-Semax",
      "P-21",
      "NSI-189",
      "Noopept",
      "Piracetam",
      "Phenylpiracetam",
      "Methylene Blue",
      "Lion's Mane Mushroom",
      "Bacopa monnieri",
      "Uridine Monophosphate (UMP)",
      "Alpha-GPC",
      "CDP-Choline",
      "Sulbutiamine",
      "TAK-653",
      "PE-22-28",
      "Sabroxy (Oroxylin A)",
      "9-MBC (9-Methyl-β-carboline)",
    ],
  },
  {
    slug: "longevity",
    label: "Longevity & Anti-Aging",
    emoji: "⏳",
    description: "Compounds studied for senescence, healthspan extension, and age-related decline.",
    compounds: [
      "Rapamycin",
      "Metformin",
      "Fisetin",
      "Dasatinib",
      "FOXO4-DRI",
      "Spermidine",
      "Pterostilbene",
      "Urolithin A",
      "Astaxanthin",
      "Quercetin",
      "Apigenin",
      "Hydroxytyrosol",
      "Oleocanthal",
      "Sulforaphane",
      "Curcumin",
      "Berberine",
      "EGCG (Epigallocatechin Gallate)",
      "NAD+",
      "NMN",
      "PQQ (Pyrroloquinoline Quinone)",
      "Coenzyme Q10",
      "Glutathione",
      "TUDCA",
      "NAC",
      "Alpha-Lipoic Acid",
      "Epithalon",
      "GHK-Cu",
      "Humanin",
      "MOTS-c",
      "Cartalax",
      "Vesugen",
      "Cardiogen",
      "Cortagen",
      "Vilon",
      "Pinealon",
      "Livagen",
      "Thymalin",
    ],
  },
  {
    slug: "sleep",
    label: "Sleep Optimization",
    emoji: "😴",
    description: "Compounds researched for sleep onset, deep-sleep architecture, and circadian rhythm.",
    compounds: [
      "DSIP (Delta Sleep-Inducing Peptide)",
      "Epithalon",
      "Selank",
      "Melatonin",
      "Glycine",
      "L-Theanine",
      "L-Tryptophan",
      "Magnesium",
      "Magnesium L-Threonate",
      "Apigenin",
      "Ashwagandha",
      "Pinealon",
      "Inositol",
      "Dream Spray",
    ],
  },
  {
    slug: "skin-hair",
    label: "Skin & Hair",
    emoji: "✨",
    description: "Peptides and topicals studied for dermal regeneration, hair growth, and pigmentation.",
    compounds: [
      "GHK-Cu",
      "AHK-Cu",
      "TB-500",
      "BPC-157",
      "Acetyl Hexapeptide-8",
      "Snap-8",
      "PDRN",
      "Glow Blend",
      "Melanotan I",
      "Melanotan II",
      "RU-58841",
      "Clascoterone",
      "Finasteride",
      "Minoxidil",
      "Saw Palmetto",
      "Pygeum",
      "Biotin",
      "Collagen Peptides",
      "Hesperidin",
    ],
  },
  {
    slug: "energy",
    label: "Energy & Performance",
    emoji: "⚡",
    description: "Compounds for mitochondrial output, ATP, athletic performance, and physical energy.",
    compounds: [
      "Caffeine",
      "Modafinil",
      "Bromantane",
      "Theacrine",
      "DMHA",
      "Creatine",
      "MOTS-c",
      "SS-31",
      "BAM15",
      "ITPP",
      "Coenzyme Q10",
      "L-Carnitine",
      "NAD+",
      "NMN",
      "PQQ (Pyrroloquinoline Quinone)",
      "Shilajit",
      "Cordyceps",
      "Rhodiola rosea",
      "Eleuthero",
      "Panax ginseng",
      "Tongkat Ali",
      "Astragalus (Huang Qi)",
    ],
  },
  {
    slug: "stress",
    label: "Stress & Mood",
    emoji: "🧘",
    description: "Anxiolytic peptides, adaptogens, and mood-stabilizing compounds.",
    compounds: [
      "Selank",
      "N-Acetyl-Selank",
      "Semax",
      "DSIP (Delta Sleep-Inducing Peptide)",
      "Phenibut",
      "Kavain",
      "Ashwagandha",
      "L-Theanine",
      "Magnesium",
      "Magnesium L-Threonate",
      "Glycine",
      "Inositol",
      "Bromantane",
      "Rhodiola rosea",
      "Schisandra",
      "Tulsi (Holy Basil)",
      "Eleuthero",
      "Hawthorn",
      "Pinealon",
      "Cortagen",
    ],
  },
  {
    slug: "dopamine",
    label: "Dopamine & Motivation",
    emoji: "🎯",
    description: "Dopamine-supportive peptides and motivation-boosting compounds.",
    compounds: [
      "Semax",
      "N-Acetyl-Semax",
      "Selank",
      "Bromantane",
      "Sulbutiamine",
      "Modafinil",
      "Phenylpiracetam",
      "Mucuna Pruriens",
      "L-Tyrosine",
      "Dihexa",
      "Methylene Blue",
      "Theacrine",
      "Caffeine",
      "Rhodiola rosea",
    ],
  },
  {
    slug: "social",
    label: "Social Performance",
    emoji: "🗣️",
    description: "Anxiolytic peptides and confidence-enhancing compounds for social settings.",
    compounds: [
      "Selank",
      "N-Acetyl-Selank",
      "Oxytocin",
      "Semax",
      "Bromantane",
      "Phenibut",
      "Kavain",
      "L-Theanine",
      "Ashwagandha",
      "PT-141 (Bremelanotide)",
    ],
  },
  {
    slug: "neuroplasticity",
    label: "BDNF & Neuroplasticity",
    emoji: "🧬",
    description: "Compounds and protocols for brain-derived neurotrophic factor and neural growth.",
    compounds: [
      "Dihexa",
      "Semax",
      "N-Acetyl-Semax",
      "Selank",
      "P-21",
      "NSI-189",
      "TAK-653",
      "PE-22-28",
      "Methylene Blue",
      "Lion's Mane Mushroom",
      "Bacopa monnieri",
      "Sabroxy (Oroxylin A)",
      "9-MBC (9-Methyl-β-carboline)",
      "Noopept",
      "Piracetam",
      "Phenylpiracetam",
      "Uridine Monophosphate (UMP)",
      "Sulbutiamine",
      "Alpha-GPC",
      "CDP-Choline",
    ],
  },
];
